import React, { useState, useEffect } from 'react';
import { 
  Settings as SettingsIcon, Save, Building, Clock, 
  DollarSign, Bell, Shield, Database, Printer, Mail,
  Smartphone, Globe, ToggleLeft, ToggleRight, AlertCircle,
  RefreshCw, Download, Upload, Trash2
} from 'lucide-react';
import toast from 'react-hot-toast';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    // General Settings
    clinic_name: 'Nutriaxis Nutrition Consultancy & Wellness Clinic',
    clinic_address: 'Nairobi, Kenya',
    clinic_phone: '+254 700 000000',
    clinic_whatsapp: '+254 700 000000',
    clinic_email: 'info@nutriaxis.com',
    clinic_website: 'www.nutriaxis.com',
    clinic_pin: '',
    clinic_logo: '/nutriaxis-logo.png',
    
    // Business Settings
    currency: 'KES',
    tax_rate: '16',
    tax_name: 'VAT',
    low_stock_alert: '5',
    expiry_alert_days: '30',
    
    // Operating Hours
    opening_time: '08:00',
    closing_time: '17:00',
    working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    
    // Notification Settings
    email_notifications: true,
    sms_notifications: false,
    appointment_reminders: true,
    stock_alerts: true,
    
    // Receipt Settings
    receipt_header: 'Nutriaxis Clinic',
    receipt_footer: 'Thank you for choosing Nutriaxis!',
    receipt_show_logo: true,
    auto_print_receipt: true,
    
    // Backup & Data
    auto_backup: false,
    backup_frequency: 'daily',
    data_retention_days: '365'
  });

  const [workingDays, setWorkingDays] = useState([
    { key: 'monday', label: 'Monday', selected: true },
    { key: 'tuesday', label: 'Tuesday', selected: true },
    { key: 'wednesday', label: 'Wednesday', selected: true },
    { key: 'thursday', label: 'Thursday', selected: true },
    { key: 'friday', label: 'Friday', selected: true },
    { key: 'saturday', label: 'Saturday', selected: false },
    { key: 'sunday', label: 'Sunday', selected: false }
  ]);

  const getAuthToken = () => localStorage.getItem('token');

  const apiFetchSettings = async () => {
    const token = getAuthToken();
    const res = await fetch('/api/settings', {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!res.ok) throw new Error('Failed to load settings');
    return res.json();
  };

  const apiUpsertSettings = async () => {
    const token = getAuthToken();
    const res = await fetch('/api/settings', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ settings })
    });
    if (!res.ok) throw new Error('Failed to save settings');
    return res.json();
  };


  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { settings: saved } = await apiFetchSettings();
        if (!mounted) return;
        if (saved && typeof saved === 'object') {
          setSettings((prev) => ({ ...prev, ...saved }));
        }
      } catch (e) {
        // First run (no settings row yet) is fine.
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const handleSaveGeneral = async () => {
    setLoading(true);
    try {
      await apiUpsertSettings();
      toast.success('General settings saved successfully!');
      // Reload from backend so the UI always reflects persisted settings
      const { settings: saved } = await apiFetchSettings();
      setSettings((prev) => ({ ...prev, ...saved }));
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };


  const handleSaveNotifications = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      localStorage.setItem('notification_settings', JSON.stringify({
        email_notifications: settings.email_notifications,
        sms_notifications: settings.sms_notifications,
        appointment_reminders: settings.appointment_reminders,
        stock_alerts: settings.stock_alerts
      }));
      toast.success('Notification settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveReceipt = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Receipt settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleBackup = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      toast.success('Backup completed successfully!');
    } catch (error) {
      toast.error('Backup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = () => {
    if (window.confirm('This will restore data from the last backup. Current data may be lost. Continue?')) {
      toast.success('Data restored successfully!');
    }
  };

  const handleClearData = () => {
    if (window.confirm('WARNING: This will permanently delete all data. This action cannot be undone. Are you sure?')) {
      if (window.confirm('FINAL WARNING: Type "DELETE" to confirm')) {
        toast.error('Data cleared (demo only - not actually deleted)');
      }
    }
  };

  const tabs = [
    { id: 'general', label: 'General', icon: Building },
    { id: 'business', label: 'Business', icon: DollarSign },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'receipt', label: 'Receipt', icon: Printer },
    { id: 'backup', label: 'Backup & Data', icon: Database },
    { id: 'security', label: 'Security', icon: Shield }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Settings</h1>
        <p className="text-gray-600 mt-1">Configure your clinic management system</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b">
          <nav className="flex overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-4 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'border-green-600 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon size={18} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* General Settings */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Clinic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Clinic Name</label>
                    <input
                      type="text"
                      value={settings.clinic_name}
                      onChange={(e) => setSettings({...settings, clinic_name: e.target.value})}
                      className="w-full p-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Phone Number</label>
                    <input
                      type="text"
                      value={settings.clinic_phone}
                      onChange={(e) => setSettings({...settings, clinic_phone: e.target.value})}
                      className="w-full p-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Email</label>
                    <input
                      type="email"
                      value={settings.clinic_email}
                      onChange={(e) => setSettings({...settings, clinic_email: e.target.value})}
                      className="w-full p-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">WhatsApp Number</label>
                    <input
                      type="text"
                      value={settings.clinic_whatsapp}
                      onChange={(e) => setSettings({...settings, clinic_whatsapp: e.target.value})}
                      className="w-full p-2 border rounded-lg"
                      placeholder="+254 700 000000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Website</label>
                    <input
                      type="text"
                      value={settings.clinic_website}
                      onChange={(e) => setSettings({...settings, clinic_website: e.target.value})}
                      className="w-full p-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">KRA PIN</label>
                    <input
                      type="text"
                      value={settings.clinic_pin}
                      onChange={(e) => setSettings({...settings, clinic_pin: e.target.value})}
                      className="w-full p-2 border rounded-lg"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2">Company Logo</label>
                    <div className="flex items-start gap-4">
                      <div className="flex-1">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setSettings({...settings, clinic_logo: reader.result});
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          className="w-full p-2 border rounded-lg"
                        />
                        <p className="text-xs text-gray-500 mt-1">Upload logo image (PNG, JPG, SVG)</p>
                      </div>
                      {settings.clinic_logo && (
                        <div className="w-24 h-24 border-2 border-gray-200 rounded-lg p-2 flex items-center justify-center bg-gray-50">
                          <img src={settings.clinic_logo} alt="Logo Preview" className="max-w-full max-h-full object-contain" />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">Address</label>
                    <textarea
                      value={settings.clinic_address}
                      onChange={(e) => setSettings({...settings, clinic_address: e.target.value})}
                      className="w-full p-2 border rounded-lg"
                      rows="2"
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4">Operating Hours</h3>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Opening Time</label>
                      <input
                        type="time"
                        value={settings.opening_time}
                        onChange={(e) => setSettings({...settings, opening_time: e.target.value})}
                        className="w-full p-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Closing Time</label>
                      <input
                        type="time"
                        value={settings.closing_time}
                        onChange={(e) => setSettings({...settings, closing_time: e.target.value})}
                        className="w-full p-2 border rounded-lg"
                      />
                    </div>
                  </div>
                
                <div className="mt-4">
                  <label className="block text-sm font-medium mb-2">Working Days</label>
                  <div className="flex flex-wrap gap-2">
                    {workingDays.map((day) => (
                      <button
                        key={day.key}
                        onClick={() => {
                          const updated = workingDays.map(d => 
                            d.key === day.key ? {...d, selected: !d.selected} : d
                          );
                          setWorkingDays(updated);
                          setSettings({
                            ...settings,
                            working_days: updated.filter(d => d.selected).map(d => d.key)
                          });
                        }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          day.selected
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleSaveGeneral}
                  disabled={loading}
                  className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  <Save size={18} />
                  <span>{loading ? 'Saving...' : 'Save General Settings'}</span>
                </button>
              </div>
            </div>
          )}

          {/* Business Settings */}
          {activeTab === 'business' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Financial Settings</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Currency</label>
                    <select
                      value={settings.currency}
                      onChange={(e) => setSettings({...settings, currency: e.target.value})}
                      className="w-full p-2 border rounded-lg"
                    >
                      <option value="KES">KES - Kenyan Shilling</option>
                      <option value="USD">USD - US Dollar</option>
                      <option value="EUR">EUR - Euro</option>
                      <option value="GBP">GBP - British Pound</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Tax Rate (%)</label>
                    <input
                      type="number"
                      value={settings.tax_rate}
                      onChange={(e) => setSettings({...settings, tax_rate: e.target.value})}
                      className="w-full p-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Tax Name</label>
                    <input
                      type="text"
                      value={settings.tax_name}
                      onChange={(e) => setSettings({...settings, tax_name: e.target.value})}
                      className="w-full p-2 border rounded-lg"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4">Inventory Alerts</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Low Stock Alert Level</label>
                    <input
                      type="number"
                      value={settings.low_stock_alert}
                      onChange={(e) => setSettings({...settings, low_stock_alert: e.target.value})}
                      className="w-full p-2 border rounded-lg"
                    />
                    <p className="text-xs text-gray-500 mt-1">Alert when stock falls below this number</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Expiry Alert (Days)</label>
                    <input
                      type="number"
                      value={settings.expiry_alert_days}
                      onChange={(e) => setSettings({...settings, expiry_alert_days: e.target.value})}
                      className="w-full p-2 border rounded-lg"
                    />
                    <p className="text-xs text-gray-500 mt-1">Alert before products expire (in days)</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleSaveGeneral}
                  disabled={loading}
                  className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  <Save size={18} />
                  <span>Save Business Settings</span>
                </button>
              </div>
            </div>
          )}

          {/* Notification Settings */}
          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Notification Preferences</h3>
                
                <div className="space-y-4">
                  {/* Email Notifications */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Mail size={20} className="text-gray-600" />
                      <div>
                        <p className="font-medium">Email Notifications</p>
                        <p className="text-sm text-gray-600">Receive notifications via email</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSettings({...settings, email_notifications: !settings.email_notifications})}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.email_notifications ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.email_notifications ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>

                  {/* SMS Notifications */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Smartphone size={20} className="text-gray-600" />
                      <div>
                        <p className="font-medium">SMS Notifications</p>
                        <p className="text-sm text-gray-600">Send SMS for appointments and reminders</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSettings({...settings, sms_notifications: !settings.sms_notifications})}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.sms_notifications ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.sms_notifications ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>

                  {/* Appointment Reminders */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Bell size={20} className="text-gray-600" />
                      <div>
                        <p className="font-medium">Appointment Reminders</p>
                        <p className="text-sm text-gray-600">Auto-remind clients of upcoming appointments</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSettings({...settings, appointment_reminders: !settings.appointment_reminders})}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.appointment_reminders ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.appointment_reminders ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>

                  {/* Stock Alerts */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <AlertCircle size={20} className="text-gray-600" />
                      <div>
                        <p className="font-medium">Stock Alerts</p>
                        <p className="text-sm text-gray-600">Get notified when stock is low</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSettings({...settings, stock_alerts: !settings.stock_alerts})}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.stock_alerts ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.stock_alerts ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleSaveNotifications}
                  disabled={loading}
                  className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  <Save size={18} />
                  <span>Save Notification Settings</span>
                </button>
              </div>
            </div>
          )}

          {/* Receipt Settings */}
          {activeTab === 'receipt' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Receipt Configuration</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Receipt Header</label>
                    <input
                      type="text"
                      value={settings.receipt_header}
                      onChange={(e) => setSettings({...settings, receipt_header: e.target.value})}
                      className="w-full p-2 border rounded-lg"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Receipt Footer</label>
                    <input
                      type="text"
                      value={settings.receipt_footer}
                      onChange={(e) => setSettings({...settings, receipt_footer: e.target.value})}
                      className="w-full p-2 border rounded-lg"
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Show Logo on Receipt</p>
                      <p className="text-sm text-gray-600">Display clinic logo on printed receipts</p>
                    </div>
                    <button
                      onClick={() => setSettings({...settings, receipt_show_logo: !settings.receipt_show_logo})}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.receipt_show_logo ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.receipt_show_logo ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Auto-Print Receipt</p>
                      <p className="text-sm text-gray-600">Automatically print receipt after sale</p>
                    </div>
                    <button
                      onClick={() => setSettings({...settings, auto_print_receipt: !settings.auto_print_receipt})}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.auto_print_receipt ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.auto_print_receipt ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                </div>

                {/* Receipt Preview */}
                <div className="mt-6 p-4 border-2 border-dashed border-gray-300 rounded-lg">
                  <h4 className="font-medium mb-3 text-center">Receipt Preview</h4>
                  <div className="bg-white p-4 max-w-xs mx-auto border">
                    <div className="text-center mb-3">
                      <p className="font-bold">{settings.receipt_header}</p>
                      <p className="text-xs">{settings.clinic_address}</p>
                      <p className="text-xs">{settings.clinic_phone}</p>
                    </div>
                    <div className="border-t border-b border-dashed py-2 my-2">
                      <div className="flex justify-between text-xs">
                        <span>Sample Product x1</span>
                        <span>KES 1,000</span>
                      </div>
                    </div>
                    <div className="text-center mt-3">
                      <p className="text-xs">{settings.receipt_footer}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleSaveReceipt}
                  disabled={loading}
                  className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  <Save size={18} />
                  <span>Save Receipt Settings</span>
                </button>
              </div>
            </div>
          )}

          {/* Backup & Data */}
          {activeTab === 'backup' && (
            <div className="space-y-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle size={20} className="text-yellow-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-800">Important</p>
                    <p className="text-sm text-yellow-700">
                      Regular backups are essential to prevent data loss. 
                      Download backups regularly and store them securely.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Backup Now */}
                <div className="bg-white border rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Download size={24} className="text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Backup Data</h3>
                      <p className="text-sm text-gray-600">Download all clinic data</p>
                    </div>
                  </div>
                  <button
                    onClick={handleBackup}
                    disabled={loading}
                    className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Backing up...' : 'Create Backup Now'}
                  </button>
                </div>

                {/* Restore */}
                <div className="bg-white border rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Upload size={24} className="text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Restore Data</h3>
                      <p className="text-sm text-gray-600">Restore from previous backup</p>
                    </div>
                  </div>
                  <button
                    onClick={handleRestore}
                    className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700"
                  >
                    Restore from Backup
                  </button>
                </div>
              </div>

              {/* Auto Backup Settings */}
              <div className="bg-white border rounded-lg p-6">
                <h3 className="font-semibold mb-4">Auto Backup Settings</h3>
                
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="font-medium">Automatic Backups</p>
                    <p className="text-sm text-gray-600">Schedule automatic data backups</p>
                  </div>
                  <button
                    onClick={() => setSettings({...settings, auto_backup: !settings.auto_backup})}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.auto_backup ? 'bg-green-600' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.auto_backup ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {settings.auto_backup && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Backup Frequency</label>
                    <select
                      value={settings.backup_frequency}
                      onChange={(e) => setSettings({...settings, backup_frequency: e.target.value})}
                      className="w-full p-2 border rounded-lg"
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Danger Zone */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <h3 className="font-semibold text-red-800 mb-4">Danger Zone</h3>
                <p className="text-sm text-red-600 mb-4">
                  These actions are irreversible. Please proceed with caution.
                </p>
                <button
                  onClick={handleClearData}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center space-x-2"
                >
                  <Trash2 size={18} />
                  <span>Clear All Data</span>
                </button>
              </div>
            </div>
          )}

          {/* Security Settings */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Security Settings</h3>
                
                <div className="space-y-4">
                  <div className="bg-white border rounded-lg p-6">
                    <h4 className="font-medium mb-3">Change Password</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm mb-1">Current Password</label>
                        <input type="password" className="w-full p-2 border rounded-lg" />
                      </div>
                      <div>
                        <label className="block text-sm mb-1">New Password</label>
                        <input type="password" className="w-full p-2 border rounded-lg" />
                      </div>
                      <div>
                        <label className="block text-sm mb-1">Confirm New Password</label>
                        <input type="password" className="w-full p-2 border rounded-lg" />
                      </div>
                      <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
                        Update Password
                      </button>
                    </div>
                  </div>

                  <div className="bg-white border rounded-lg p-6">
                    <h4 className="font-medium mb-3">Session Management</h4>
                    <p className="text-sm text-gray-600 mb-3">
                      You are currently logged in. You can log out of all other sessions.
                    </p>
                    <button className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700">
                      Logout All Other Sessions
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;